import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createManualCaixaTransaction,
  listCaixaSupplierSummaries,
  listSupplierCaixaTransactions,
  undoCaixaTransaction,
} from "./api";
import type {
  CaixaSupplierSummaryParams,
  CaixaTransactionsParams,
  ManualCaixaTransactionPayload,
} from "./types";
import {
  CAIXAS_KEY,
  CAIXAS_SUPPLIER_SUMMARIES_KEY,
  invalidateCaixaQueries,
} from "./queryKeys";

export { CAIXAS_KEY, CAIXAS_SUPPLIER_SUMMARIES_KEY, invalidateCaixaQueries };

export function useCaixaSupplierSummaries(params: CaixaSupplierSummaryParams) {
  return useQuery({
    queryKey: [...CAIXAS_SUPPLIER_SUMMARIES_KEY, params],
    queryFn: ({ signal }) => listCaixaSupplierSummaries(params, signal),
    placeholderData: keepPreviousData,
  });
}

export function useSupplierCaixaTransactions(
  supplierId: string | null,
  params: CaixaTransactionsParams,
) {
  const queryKey = [...CAIXAS_KEY, "supplier-transactions", supplierId, params];
  return useQuery({
    queryKey,
    queryFn: ({ signal }) =>
      listSupplierCaixaTransactions(supplierId!, params, signal),
    enabled: Boolean(supplierId),
    placeholderData: (previousData, previousQuery) => {
      const previousKey = previousQuery?.queryKey;
      const previousSupplierId = previousKey?.[previousKey.length - 2];
      return previousSupplierId === supplierId ? previousData : undefined;
    },
  });
}

export function useCreateManualCaixaTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: ManualCaixaTransactionPayload;
    }) => createManualCaixaTransaction(id, payload),
    onSuccess: () => invalidateCaixaQueries(qc),
  });
}

export function useUndoCaixaTransaction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => undoCaixaTransaction(id),
    onSuccess: () => invalidateCaixaQueries(qc),
  });
}
